/**
 * A read-only field list: each item renders its label above its value, two
 * to a row, optionally grouped into collapsible sections.
 *
 * `tooltip` and `pendingValue` came from the card drawers (OUT-1404), which
 * had their own copy of this pairing. Both are optional, so nothing else
 * changes.
 *
 * The package ships no read-only field list, so the label/value pairing is
 * composed here from theme variants and colour tokens. A `FieldList` /
 * `DetailGrid` brief is the real answer; see the OUT-1120 notes in
 * DS_MIGRATION.md.
 */
import React from "react";
import Box from "@mui/material/Box";
import Card from "@mui/material/Card";
import MuiLink from "@mui/material/Link";
import Typography from "@mui/material/Typography";
import Tooltip from "@mui/material/Tooltip";
import { HugeiconsIcon } from "@hugeicons/react";
import { InformationCircleIcon } from "@hugeicons-pro/core-stroke-rounded";
import { useTranslation } from "react-i18next";
import { Accordion } from "@outpave/ds";
import { Inline, Stack } from "components/layout/reusableLayouts";
import CustomSkeleton from "./CustomSkeleton";

export interface SummaryItem {
  /** Translated when it is a string; pass a node when the label needs one. */
  title: React.ReactNode;
  value: React.ReactNode;
  onClick?: () => void;
  groupBy?: string;
  /**
   * Give this field the whole row instead of a half column. Set it on values
   * that read badly at half width: prose, addresses, emails, URLs.
   */
  fullWidth?: boolean;
  /** Hint beside the label. A node, so it can be a list rather than a line. */
  tooltip?: React.ReactNode;
  /**
   * A pending replacement for `value`: the current value strikes through and
   * this renders beneath it. For approval flows, where the point of the field
   * is what is about to change.
   */
  pendingValue?: React.ReactNode;
}

export interface SummaryProps {
  items: SummaryItem[];
  children?: React.ReactNode;
  /** Render the fields bare, without the outlined Card container. */
  disableContainer?: boolean;
  /** Replace content with a skeleton placeholder. */
  loading?: boolean;
  /** When `loading`, also show a circular icon placeholder. */
  showLoadingIcon?: boolean;
  /**
   * Right-align the fields that land in the second column, so each row reads
   * as a label/value pair anchored to its own edge. Spanning fields stay left,
   * because a full-width value has no partner to balance against.
   */
  alignEndColumn?: boolean;
}

const isRenderableValue = (value: React.ReactNode): boolean => {
  // Treat 0 as a real value worth rendering. The old `!value` guard
  // dropped any zero count / amount / index.
  if (value == null || value === "" || value === false) return false;
  if (React.isValidElement(value) && "text" in value.props) {
    const text = (value.props as { text?: unknown }).text;
    if (!text || (Array.isArray(text) && text.every((entry) => !entry))) {
      return false;
    }
  }
  return true;
};

const surfaceSx = {
  display: "flex",
  flexDirection: "column",
  padding: "var(--space-5)",
  minWidth: 0,
} as const;

const fieldGridSx = {
  display: "grid",
  gridTemplateColumns: "repeat(2, 1fr)",
  rowGap: "var(--space-6)",
  columnGap: "var(--space-7)",
} as const;

// `minWidth: 0` holds the two columns at half and half: a grid item's
// automatic minimum is min-content, so without it a long address or
// description widens its own track and the halves stop matching.
const fieldSx = {
  display: "flex",
  flexDirection: "column",
  gap: "var(--space-1)",
  minWidth: 0,
} as const;

// An odd trailing field has no partner to share its row with, so it spans
// rather than leaving a hole in the second column.
const spanFieldSx = {
  ...fieldSx,
  gridColumn: "1 / -1",
} as const;

const labelSx = {
  color: "var(--color-text-tertiary)",
} as const;

const valueSx = {
  color: "var(--color-text-primary)",
  overflowWrap: "break-word",
} as const;

const supersededValueSx = {
  ...valueSx,
  textDecoration: "line-through",
  color: "var(--color-text-tertiary)",
} as const;

// Which fields span the row. A `fullWidth` field always does; a trailing field
// spans when nothing is left to pair it with, so grid placement has to be
// walked rather than read off index parity: one spanning field mid-list shifts
// every column after it.
const resolvePlacement = (
  fields: SummaryItem[],
): { spans: boolean[]; columns: number[] } => {
  const spans = fields.map((field) => Boolean(field.fullWidth));
  const columns = fields.map(() => 0);
  let column = 0;
  fields.forEach((_, index) => {
    if (spans[index]) {
      column = 0;
      return;
    }
    if (column === 0 && index === fields.length - 1) {
      spans[index] = true;
      return;
    }
    columns[index] = column;
    column = (column + 1) % 2;
  });
  return { spans, columns };
};

const endColumnSx = {
  alignItems: "flex-end",
  textAlign: "right",
} as const;

const Summary: React.FC<SummaryProps> = ({
  items,
  children,
  disableContainer,
  loading,
  showLoadingIcon,
  alignEndColumn = false,
}) => {
  const { t } = useTranslation();

  const filteredItems = items.filter((item) => isRenderableValue(item.value));

  // Group items by their `groupBy` key. Ungrouped items collect under
  // a sentinel key so they can be rendered first / inline.
  const groupedItems = filteredItems.reduce<Record<string, SummaryItem[]>>(
    (acc, item) => {
      const key = item.groupBy ?? "ungrouped";
      if (!acc[key]) acc[key] = [];
      acc[key].push(item);
      return acc;
    },
    {},
  );

  const renderValue = (item: SummaryItem): React.ReactNode => {
    // Values are data, not keys, so the separators have to be off: i18next
    // reads ":" as a namespace and "." as a nesting separator by default, and
    // a value like "05/24/2026 · 9:41 AM" comes back as "41 AM". Same trap the
    // key rule in DS_MIGRATION.md guards, arriving through the value side.
    const valueNode =
      typeof item.value === "string"
        ? t(item.value, { nsSeparator: false, keySeparator: false })
        : item.value;

    if (item.onClick) {
      // `type` is load-bearing: several call sites sit inside a Formik form,
      // where a bare button would submit it.
      //
      // A button and not an anchor: an `a` with no `href` has no role and
      // cannot be focused, so the value would be unreachable by keyboard and
      // announce as plain text. The overrides are what an anchor gave for
      // free, since a button neither wraps its text nor aligns it left.
      return (
        <MuiLink
          component="button"
          type="button"
          onClick={item.onClick}
          sx={{ whiteSpace: "normal", textAlign: "left" }}
        >
          {valueNode}
        </MuiLink>
      );
    }
    return valueNode;
  };

  const renderFields = (fields: SummaryItem[], groupIndex: number) => {
    const { spans, columns } = resolvePlacement(fields);
    return fields.map((field, index) => (
      <Box
        key={`${groupIndex}-${index}`}
        sx={{
          ...(spans[index] ? spanFieldSx : fieldSx),
          ...(alignEndColumn && !spans[index] && columns[index] === 1
            ? endColumnSx
            : {}),
        }}
      >
        {/* No wrapper without a tooltip: a label's parent is its own cell,
            which is the relation Summary.test.tsx pins. */}
        {field.tooltip ? (
          <Inline gap="var(--space-2)" wrap={false} sx={labelSx}>
            <Typography variant="caption" component="div" sx={labelSx}>
              {typeof field.title === "string" ? t(field.title) : field.title}
            </Typography>
            {/* MUI shows a Tooltip on focus or hover of its CHILD, and an
                svg is neither focusable nor nameable, so the hint was
                mouse-only. The span carries the tab stop and the name. */}
            <Tooltip title={field.tooltip}>
              <Box
                component="span"
                tabIndex={0}
                role="button"
                aria-label={t("More information")}
                sx={{ display: "inline-flex", cursor: "help" }}
              >
                <HugeiconsIcon icon={InformationCircleIcon} size={14} />
              </Box>
            </Tooltip>
          </Inline>
        ) : (
          <Typography variant="caption" component="div" sx={labelSx}>
            {typeof field.title === "string" ? t(field.title) : field.title}
          </Typography>
        )}
        <Typography
          variant="body1"
          component="div"
          sx={field.pendingValue ? supersededValueSx : valueSx}
        >
          {renderValue(field)}
        </Typography>
        {field.pendingValue && (
          <Typography variant="body1" component="div" sx={valueSx}>
            {field.pendingValue}
          </Typography>
        )}
      </Box>
    ));
  };

  const content = (
    <Stack gap="var(--space-5)">
      {Object.entries(groupedItems).map(([group, groupItems], groupIndex) =>
        group === "ungrouped" ? (
          <Box key={group} sx={fieldGridSx}>
            {renderFields(groupItems, groupIndex)}
          </Box>
        ) : (
          <Accordion key={group} title={t(group)}>
            <Box sx={fieldGridSx}>{renderFields(groupItems, groupIndex)}</Box>
          </Accordion>
        ),
      )}
      {children && <Box>{children}</Box>}
    </Stack>
  );

  const skeleton = (
    <CustomSkeleton rows={6} hideCircular={!showLoadingIcon} gap={2} />
  );

  if (disableContainer) {
    return loading ? skeleton : content;
  }

  return (
    <Card variant="outlined" sx={surfaceSx}>
      {loading ? skeleton : content}
    </Card>
  );
};

export default Summary;
